import { useEffect } from "react";

const SITE = "https://pepuralabs.ca";
const DEFAULT_IMAGE = `${SITE}/Pepura-icon-header.png`;
const MANAGED = "data-seo-managed";

function upsert(attr, key, content) {
  if (!content) return;
  // Prefer an existing managed tag, otherwise ADOPT the static tag from
  // index.html (marking it managed) instead of appending a duplicate —
  // two <meta name="description"> tags confuse crawlers.
  let el =
    document.head.querySelector(`meta[${attr}="${key}"][${MANAGED}]`) ||
    document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED, "1");
  el.setAttribute("content", content);
}

/**
 * Per-page SEO meta manager for the SPA. Sets document.title, the meta
 * description, canonical link, and Open Graph / Twitter card tags.
 * Managed tags are marked with data-seo-managed so pages cleanly
 * overwrite each other while the static index.html defaults stay
 * untouched for crawlers that don't run JS.
 *
 * Admin-editable overrides (Admin → SEO) are fetched once per session
 * from /api/seo-meta and take precedence over the per-page defaults.
 * Override titles are used VERBATIM (no "| Pepura Labs" suffix).
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let _overrides = null;
let _overridesPromise = null;

function fetchOverrides() {
  if (_overrides) return Promise.resolve(_overrides);
  if (_overridesPromise) return _overridesPromise;
  _overridesPromise = fetch(`${API}/seo-meta`)
    .then((r) => r.json())
    .then((d) => {
      _overrides = d.overrides || {};
      return _overrides;
    })
    .catch(() => {
      _overridesPromise = null;
      return {};
    });
  return _overridesPromise;
}

export const Seo = ({ title, description, path = "/", image, type = "website" }) => {
  useEffect(() => {
    let cancelled = false;
    const url = `${SITE}${path}`;
    const img = image
      ? image.startsWith("http")
        ? image
        : `${SITE}${image}`
      : DEFAULT_IMAGE;

    const apply = (t, d, verbatim) => {
      let fullTitle = verbatim
        ? t
        : t
          ? `${t} | Pepura Labs`
          : "Pepura Labs — Retatrutide in Canada";
      // Crawlers flag titles > 60 chars — drop the brand suffix if needed.
      if (!verbatim && t && fullTitle.length > 60) fullTitle = t;
      document.title = fullTitle;
      upsert("name", "description", d);
      upsert("property", "og:site_name", "Pepura Labs");
      upsert("property", "og:title", fullTitle);
      upsert("property", "og:description", d);
      upsert("property", "og:type", type);
      upsert("property", "og:url", url);
      upsert("property", "og:image", img);
      upsert("name", "twitter:card", "summary_large_image");
      upsert("name", "twitter:title", fullTitle);
      upsert("name", "twitter:description", d);
      upsert("name", "twitter:image", img);
    };

    apply(title, description, false);
    fetchOverrides().then((ov) => {
      const o = ov?.[path];
      if (cancelled || !o || (!o.title && !o.description)) return;
      apply(
        o.title || (title ? `${title} | Pepura Labs` : document.title),
        o.description || description,
        true,
      );
    });

    let canonical = document.head.querySelector(`link[rel="canonical"]`);
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    return () => {
      cancelled = true;
    };
  }, [title, description, path, image, type]);

  return null;
};
